import { useState, useEffect, useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, ArrowRight, MapPin, Star } from "lucide-react";
import { SEOHead, breadcrumbSchema, localBusinessSchema, reviewSchema } from "../components/SEOHead";
import Breadcrumbs from "../components/Breadcrumbs";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Map area slugs to suburb keywords for testimonial matching
const AREA_KEYWORDS = {
  "web-design-bayside-melbourne": ["brighton", "sandringham", "hampton", "bayside"],
  "web-design-south-melbourne": ["south melbourne", "southbank"],
  "web-design-st-kilda": ["st kilda", "elwood", "port phillip"],
  "web-design-richmond": ["richmond", "cremorne"],
  "web-design-fitzroy-collingwood": ["fitzroy", "collingwood"],
  "web-design-brunswick-coburg": ["brunswick", "coburg", "pascoe vale"],
  "web-design-prahran-south-yarra": ["prahran", "south yarra"],
  "web-design-hawthorn-camberwell": ["hawthorn", "camberwell", "kew"],
  "web-design-geelong": ["geelong"],
  "web-design-mornington-peninsula": ["mornington", "frankston", "dromana", "sorrento"],
  "web-design-melbourne-cbd": ["melbourne cbd", "cbd", "flinders", "collins"],
  "web-design-frankston-south-east": ["frankston", "dandenong", "cranbourne", "berwick", "narre warren"],
};

export default function AreaDetailPage() {
  const { slug } = useParams();
  const [location, setLocation] = useState(null);
  const [related, setRelated] = useState([]);
  const [services, setServices] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/locations/${slug}`),
      axios.get(`${API}/locations`),
      axios.get(`${API}/testimonials`),
      axios.get(`${API}/services`),
    ]).then(([locRes, allRes, testRes, svcRes]) => {
      setLocation(locRes.data);
      setRelated(allRes.data.filter(l => l.slug !== slug).slice(0, 4));
      setTestimonials(testRes.data);
      setServices((svcRes.data || []).slice(0, 4));
    }).catch(() => {}).finally(() => setLoading(false));
  }, [slug]);

  // Match testimonials to this area, with local matches first
  const displayTestimonials = useMemo(() => {
    if (!testimonials.length) return [];
    const keywords = AREA_KEYWORDS[slug] || [];
    const local = [];
    const other = [];
    for (const t of testimonials) {
      const biz = (t.business_name || "").toLowerCase();
      if (keywords.some(k => biz.includes(k))) {
        local.push(t);
      } else {
        other.push(t);
      }
    }
    // Show local first, then fill with others, up to 3 total
    return [...local, ...other].slice(0, 3);
  }, [testimonials, slug]);

  if (loading) return (
    <main className="pt-16 min-h-screen flex items-center justify-center">
      <div className="text-center">
        <div className="w-12 h-12 border-4 border-black border-t-[#FFD600] rounded-full animate-spin mx-auto mb-4" />
        <p className="font-bold">Loading...</p>
      </div>
    </main>
  );

  if (!location) return (
    <main className="pt-16 min-h-screen flex items-center justify-center">
      <div className="text-center">
        <h1 className="text-4xl font-black mb-4">Area Not Found</h1>
        <Link to="/areas" className="btn-primary">View All Areas</Link>
      </div>
    </main>
  );

  return (
    <main className="pt-16" data-testid="area-detail-page">
      <SEOHead
        title={location.title}
        description={location.excerpt}
        path={`/areas/${location.slug}`}
        schema={[
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Areas", path: "/areas" },
            { name: location.title, path: `/areas/${location.slug}` },
          ]),
          localBusinessSchema(),
          ...(displayTestimonials.length ? [reviewSchema(displayTestimonials)] : []),
        ]}
      />
      <Breadcrumbs items={[
        { name: "Areas We Serve", path: "/areas" },
        { name: location.suburb, path: `/areas/${location.slug}` },
      ]} />

      <div className="container-main max-w-3xl py-16">
        <Link to="/areas" className="inline-flex items-center gap-2 font-bold text-sm mb-8 hover:text-[#0055FF] transition-colors" data-testid="back-to-areas">
          <ArrowLeft size={14} /> All Areas
        </Link>

        <div className="flex items-center gap-2 mb-4">
          <MapPin size={18} className="text-[#0055FF]" />
          <span className="text-sm font-bold text-[#0055FF] uppercase tracking-wider">{location.region}</span>
        </div>

        <h1 className="text-3xl md:text-5xl font-black mb-6 leading-tight" style={{ fontFamily: "Outfit, sans-serif" }} data-testid="area-title">
          {location.title}
        </h1>

        <p className="text-lg text-gray-600 mb-10 pb-8 border-b-2 border-black">{location.excerpt}</p>

        <div
          className="blog-content text-gray-800 leading-relaxed"
          dangerouslySetInnerHTML={{ __html: location.content }}
          data-testid="area-content"
        />

        {/* Testimonial Badges */}
        {displayTestimonials.length > 0 && (
          <div className="mt-14 mb-4" data-testid="area-testimonials">
            <div className="flex items-center gap-3 mb-6">
              <div className="flex">
                {[1,2,3,4,5].map(s => (
                  <Star key={s} size={20} className="text-[#FFD600] fill-[#FFD600]" />
                ))}
              </div>
              <span className="text-sm font-bold text-gray-500 uppercase tracking-wider">What Our Clients Say</span>
            </div>
            <div className="grid grid-cols-1 gap-4">
              {displayTestimonials.map((t, i) => (
                <div
                  key={t.id || i}
                  className="bg-[#F9FAFB] border-2 border-black rounded-xl p-5 relative"
                  style={{ boxShadow: "3px 3px 0px 0px rgba(0,0,0,1)" }}
                  data-testid={`area-testimonial-${i}`}
                >
                  <div className="flex items-start gap-4">
                    <div className="shrink-0 w-11 h-11 bg-[#0055FF] rounded-full flex items-center justify-center text-white font-black text-sm">
                      {(t.client_name || "?")[0].toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-black text-sm" style={{ fontFamily: "Outfit, sans-serif" }}>{t.client_name}</span>
                        <div className="flex">
                          {Array.from({ length: t.rating || 5 }).map((_, s) => (
                            <Star key={s} size={12} className="text-[#FFD600] fill-[#FFD600]" />
                          ))}
                        </div>
                      </div>
                      <p className="text-xs font-semibold text-gray-500 mb-2">{t.business_name}</p>
                      <p className="text-sm text-gray-700 leading-relaxed">{t.review}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-4 text-center">
              <Link to="/portfolio" className="text-sm font-bold text-[#0055FF] hover:underline" data-testid="area-see-more-reviews">
                See more client results &rarr;
              </Link>
            </div>
          </div>
        )}

        <div className="mt-16 bg-[#FFD600] border-2 border-black rounded-2xl p-8 text-center" style={{ boxShadow: "4px 4px 0px 0px rgba(0,0,0,1)" }}>
          <h3 className="text-2xl font-black mb-3" style={{ fontFamily: "Outfit, sans-serif" }}>
            Get Your {location.suburb} Business Online
          </h3>
          <p className="font-semibold mb-6">Professional websites from $499. Live in 48 hours. SEO included.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" className="btn-secondary inline-flex items-center gap-2" data-testid="area-quote-cta">
              Get a Free Quote <ArrowRight size={16} />
            </Link>
            <Link to="/packages" className="btn-primary inline-flex items-center gap-2" data-testid="area-packages-cta">
              View Packages <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </div>

      {services.length > 0 && (
        <section className="section-padding border-t-4 border-black">
          <div className="container-main">
            <h2 className="text-3xl font-black mb-2" style={{ fontFamily: "Outfit, sans-serif" }}>
              Web Design Services in {location.suburb}
            </h2>
            <p className="text-gray-600 mb-8 font-semibold">Specialist website solutions for local businesses.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {services.map((s, i) => (
                <Link key={s.id || i} to={`/services/${s.slug}`} className="group card-brutal" data-testid={`area-service-link-${i}`}>
                  <h3 className="font-black text-lg mb-2 group-hover:text-[#0055FF] transition-colors" style={{ fontFamily: "Outfit, sans-serif" }}>{s.title}</h3>
                  <p className="text-sm text-gray-600 line-clamp-2">{s.excerpt}</p>
                  <div className="flex items-center gap-1 mt-4 text-sm font-bold text-[#0055FF]">Learn More <ArrowRight size={12} /></div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {related.length > 0 && (
        <section className="section-padding bg-[#F4F4F5] border-t-4 border-black">
          <div className="container-main">
            <h2 className="text-3xl font-black mb-8" style={{ fontFamily: "Outfit, sans-serif" }}>Other Areas We Serve</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {related.map((l, i) => (
                <Link key={l.id || i} to={`/areas/${l.slug}`} className="group card-brutal" data-testid={`related-area-${i}`}>
                  <div className="flex items-center gap-1.5 mb-2">
                    <MapPin size={12} className="text-[#0055FF]" />
                    <span className="text-xs font-bold text-[#0055FF] uppercase tracking-wider">{l.region}</span>
                  </div>
                  <h3 className="font-black text-lg mb-2 group-hover:text-[#0055FF] transition-colors" style={{ fontFamily: "Outfit, sans-serif" }}>{l.suburb}</h3>
                  <p className="text-sm text-gray-600 line-clamp-2">{l.excerpt}</p>
                  <div className="flex items-center gap-1 mt-4 text-sm font-bold text-[#0055FF]">Learn More <ArrowRight size={12} /></div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </main>
  );
}
